import React from 'react';
import PixelIcons from '../components/PixelIcons.jsx';
import { useT } from '../i18n/useT.js';

// Services.jsx — 6 service cards as power-up tiles
const Services = (() => {
  const { Joystick, Bolt, Diamond, Star, Coin, Mushroom, Arrow } = PixelIcons;

  const services = [
    { code: "S-01", icon: "/assets/icons/desarrollo.png",              pixelIcon: Joystick, glow: "rgba(253,20,145,0.35)",  accent: "var(--ragross-magenta)", k: "s1" },
    { code: "S-02", icon: "/assets/icons/diseno.png",                  pixelIcon: Diamond,  glow: "rgba(0,232,203,0.35)",   accent: "var(--ragross-cyan)",    k: "s2" },
    { code: "S-03", icon: "/assets/icons/web.png",                     pixelIcon: Bolt,     glow: "rgba(253,177,21,0.30)",  accent: "var(--ragross-gold)",    k: "s3" },
    { code: "S-04", icon: "/assets/icons/branding.png",                pixelIcon: Star,     glow: "rgba(253,177,21,0.30)",  accent: "var(--ragross-gold)",    k: "s4" },
    { code: "S-05", icon: "/assets/icons/produccion-audiovisual.png",  pixelIcon: Mushroom, glow: "rgba(253,20,145,0.35)",  accent: "var(--ragross-magenta)", k: "s5" },
    { code: "S-06", icon: "/assets/icons/clases-online.png",           pixelIcon: Coin,     glow: "rgba(79,253,21,0.30)",   accent: "var(--ragross-green)",   k: "s6" },
  ];

  const svStyles = {
    section: { padding: "112px 0", position: "relative" },
    container: { maxWidth: 1280, margin: "0 auto", padding: "0 32px" },
    head: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 24, marginBottom: 56 },
    headLeft: { maxWidth: 640 },
    tag: {
      display: "inline-flex", alignItems: "center", gap: 12,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "11px", letterSpacing: "0.16em",
      color: "var(--ragross-magenta)",
    },
    title: {
      font: "var(--type-display-lg)",
      letterSpacing: "-0.015em",
      lineHeight: 1.05,
      color: "#fff",
      marginTop: 18,
      marginBottom: 0,
    },
    lead: {
      font: "var(--type-body-lg)",
      color: "var(--fg-2)",
      maxWidth: 560,
      marginTop: 20,
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 16,
    },
    card: {
      position: "relative",
      background: "var(--bg-surface)",
      border: "1px solid var(--border-soft)",
      borderRadius: "var(--radius-card)",
      padding: 20,
      display: "flex",
      flexDirection: "column",
      gap: 14,
      transition: "transform 200ms var(--ease-arcade), border-color 200ms, box-shadow 200ms",
      cursor: "pointer",
      minHeight: 280,
    },
    iconWrap: {
      width: 72, height: 72,
      background: "var(--ragross-purple-night)",
      border: "1px solid rgba(255,255,255,0.10)",
      borderRadius: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
    },
    icon: { width: 44, height: 44, objectFit: "contain" },
    code: {
      position: "absolute", top: 6, right: 8,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "8px",
      letterSpacing: "0.10em",
      color: "var(--fg-3)",
    },
    role: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "9px",
      letterSpacing: "0.10em",
    },
    name: { font: "var(--type-h2)", color: "#fff", lineHeight: 1.15, margin: 0 },
    desc: { font: "var(--type-body-sm)", color: "var(--fg-2)", flex: 1, margin: 0 },
    footer: {
      display: "flex", justifyContent: "space-between", alignItems: "center",
      paddingTop: 12, borderTop: "1px solid var(--border-soft)",
    },
    avail: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "8px", letterSpacing: "0.12em",
      color: "var(--fg-3)",
      display: "flex", alignItems: "center", gap: 6,
    },
    availDot: { width: 6, height: 6, background: "var(--ragross-green)", boxShadow: "var(--glow-green-md)" },
    more: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "9px", letterSpacing: "0.12em",
      display: "flex", alignItems: "center", gap: 6,
    },
  };

  function ServiceCard({ s }) {
    const t = useT();
    const [hover, setHover] = React.useState(false);
    const Icon = s.pixelIcon;
    return (
      <article
        style={{
          ...svStyles.card,
          transform: hover ? "translate(-2px,-2px)" : "none",
          borderColor: hover ? s.accent : svStyles.card.borderColor,
          boxShadow: hover
            ? `4px 4px 0 0 ${s.accent}, 0 0 30px ${s.glow}`
            : "var(--shadow-card)",
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div style={svStyles.iconWrap}>
          <Icon size={40}/>
          <span style={svStyles.code}>{s.code}</span>
        </div>
        <div style={{ ...svStyles.role, color: s.accent }}>{t(`svc.${s.k}.role`)}</div>
        <h3 style={svStyles.name}>{t(`svc.${s.k}.name`)}</h3>
        <p style={svStyles.desc}>{t(`svc.${s.k}.desc`)}</p>
        <div style={svStyles.footer}>
          <span style={svStyles.avail}>
            <span style={svStyles.availDot}></span>
            {t("svc.available")}
          </span>
          <span style={{ ...svStyles.more, color: s.accent }}>
            <Icon size={11}/>
            {t("svc.more")}
            <Arrow size={10} color={s.accent}/>
          </span>
        </div>
      </article>
    );
  }

  function Services() {
    const t = useT();
    return (
      <section
        id="services"
        style={svStyles.section}
        data-screen-label="03 Servicios"
      >
        <div style={svStyles.container}>
          <div style={svStyles.head}>
            <div style={svStyles.headLeft}>
              <div style={svStyles.tag}>
                <span>{t("svc.tag")}</span>
                <span style={{ width: 60, height: 1, background: "linear-gradient(90deg, var(--ragross-magenta), transparent)" }}></span>
              </div>
              <h2 style={svStyles.title} className="section-title">
                {t("svc.title1")}
                <br/>
                <span style={{ color: "var(--ragross-magenta)" }}>{t("svc.title2")}</span>
              </h2>
              <p style={svStyles.lead}>{t("svc.lead")}</p>
            </div>
          </div>
          <div style={svStyles.grid} className="responsive-grid-3">
            {services.map((s) => <ServiceCard key={s.code} s={s}/>)}
          </div>
        </div>
      </section>
    );
  }

  return Services;
})();

export default Services;
